// @ts-nocheck
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { roommateApi } from '../api/client';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer
} from 'recharts';

interface ScoreData {
  college_scores: { [key: string]: number };
  normalized_scores: { [key: string]: number };
}

interface College {
  id: number;
  name: string;
}

export default function Result() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const userIdNum = parseInt(userId || '0');

  const [scores, setScores] = useState<ScoreData | null>(null);
  const [colleges, setColleges] = useState<College[]>([]);
  const [loading, setLoading] = useState(true);

  // ============ 配对相关 state ============
  const [pairStatus, setPairStatus] = useState<any>(null);
  const [pairPreview, setPairPreview] = useState<any>(null);
  const [partnerInput, setPartnerInput] = useState('');
  const [pairLoading, setPairLoading] = useState(false);
  const [pairError, setPairError] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  // ============ 重新答题 state ============
  const [restartLoading, setRestartLoading] = useState(false);

  const fetchPairStatus = async () => {
    try {
      const res = await roommateApi.status(userIdNum);
      setPairStatus(res.data);

      // 已配对时拉预览数据(用于合并雷达图)
      if (res.data.confirmed_pair) {
        const previewRes = await roommateApi.preview(userIdNum);
        setPairPreview(previewRes.data);
      } else {
        setPairPreview(null);
      }
    } catch (e) {
      console.error('Failed to fetch pair status', e);
    }
  };

  useEffect(() => {
    Promise.all([
      api.get(`/api/results/${userId}`),
      api.get('/api/colleges'),
    ]).then(([scoreRes, collegeRes]) => {
      setScores(scoreRes.data);
      setColleges(collegeRes.data);
      setLoading(false);
    });
    fetchPairStatus();
  }, [userId]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <p className="text-gray-500">计算中...</p>
    </div>
  );

  if (!scores) return null;

  const chartData = colleges.map(c => ({
    name: c.name,
    匹配度: scores.normalized_scores[String(c.id)] || 0,
  }));

  const sorted = [...chartData].sort((a, b) => b.匹配度 - a.匹配度);

  // 配对后的归一化雷达图数据
  const getMergedChartData = () => {
    const source = pairPreview?.merged_scores;
    if (!source) return [];
    const total = Object.values(source).reduce((a: number, b: any) => a + (b || 0), 0);
    return colleges.map(c => ({
      name: c.name,
      合并匹配度: total > 0
        ? Math.round((source[String(c.id)] || 0) / total * 1000) / 10
        : 0,
    }));
  };

  // ============ 配对操作 handlers ============
  const handleInvite = async () => {
    if (!partnerInput.trim()) {
      setPairError('请输入室友学号（请保证室友为同性）');
      return;
    }
    setPairLoading(true);
    setPairError('');
    try {
      await roommateApi.invite(userIdNum, partnerInput.trim());
      setPartnerInput('');
      await fetchPairStatus();
    } catch (e: any) {
      setPairError(e.response?.data?.detail || '申请失败,请重试');
    } finally {
      setPairLoading(false);
    }
  };

  const handleAccept = async (pairId: number) => {
    setPairLoading(true);
    setPairError('');
    try {
      await roommateApi.accept(userIdNum, pairId);
      setShowPreview(false);
      await fetchPairStatus();
    } catch (e: any) {
      setPairError(e.response?.data?.detail || '操作失败');
    } finally {
      setPairLoading(false);
    }
  };

  const handleReject = async (pairId: number) => {
    if (!window.confirm('确定要拒绝该室友邀请吗?')) return;
    setPairLoading(true);
    setPairError('');
    try {
      await roommateApi.reject(userIdNum, pairId);
      setShowPreview(false);
      await fetchPairStatus();
    } catch (e: any) {
      setPairError(e.response?.data?.detail || '操作失败');
    } finally {
      setPairLoading(false);
    }
  };

  const handleDissolve = async (pairId: number, status: string) => {
    const msg = status === 'confirmed' ? '确定要解除当前配对吗?' : '确定要撤回该邀请吗?';
    if (!window.confirm(msg)) return;
    setPairLoading(true);
    setPairError('');
    try {
      await roommateApi.dissolve(userIdNum, pairId);
      await fetchPairStatus();
    } catch (e: any) {
      setPairError(e.response?.data?.detail || '操作失败');
    } finally {
      setPairLoading(false);
    }
  };

  // ============ 重新答题处理 ============
  const handleRestart = async () => {
    // 检查是否有 confirmed 配对
    if (pairStatus?.confirmed_pair) {
      const pair = pairStatus.confirmed_pair;
      const partner = pair.user1.id === userIdNum ? pair.user2 : pair.user1;

      // 第一步:提示需先解除配对
      const confirmDissolve = window.confirm(
        `你已和 ${partner.name} (${partner.student_id}) 配对。\n\n` +
        `重新答题需要先解除配对,对方也会回到未配对状态。\n\n` +
        `是否先解除配对?`
      );
      if (!confirmDissolve) return;

      // 解除配对
      setRestartLoading(true);
      try {
        await roommateApi.dissolve(userIdNum, pair.id);
      } catch (e: any) {
        alert(e.response?.data?.detail || '解除配对失败');
        setRestartLoading(false);
        return;
      }

      // 第二步:已解除,确认重做
      const confirmRestart = window.confirm(
        `已解除配对。\n\n` +
        `重新答题将清除你当前的所有答案,确认继续?`
      );
      if (!confirmRestart) {
        // 用户中途取消,但已经解除了配对 — 提示一下
        await fetchPairStatus();
        alert('配对已解除,但未重新答题。如需可重新邀请室友。');
        setRestartLoading(false);
        return;
      }
    } else {
      // 无 confirmed 配对的简单场景
      const confirmRestart = window.confirm(
        `重新答题将清除你当前的所有答案,确认继续?`
      );
      if (!confirmRestart) return;
      setRestartLoading(true);
    }

    // 执行重置
    try {
      await api.post('/api/responses/restart', { user_id: userIdNum });
      navigate('/quiz');
    } catch (e: any) {
      alert(e.response?.data?.detail || '重置失败,请重试');
      setRestartLoading(false);
    }
  };

  // ============ 配对区块渲染 ============
  const renderPairSection = () => {
    if (!pairStatus) {
      return (
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-4">
          <p className="text-gray-500 text-center">加载配对状态...</p>
        </div>
      );
    }

    // —————————— 已配对 ——————————
    if (pairStatus.confirmed_pair) {
      const pair = pairStatus.confirmed_pair;
      const partner = pair.user1.id === userIdNum ? pair.user2 : pair.user1;
      const mergedData = getMergedChartData();

      return (
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800">✅ 已配对室友</h2>
            <button
              onClick={() => handleDissolve(pair.id, 'confirmed')}
              disabled={pairLoading}
              className="text-sm text-red-500 hover:text-red-700 disabled:opacity-50"
            >
              解除配对
            </button>
          </div>

          <div className="bg-indigo-50 rounded-lg p-4 mb-4">
            <p className="text-gray-700">
              你的室友:<span className="font-semibold">{partner.name}</span>
              <span className="text-gray-500 ml-2">({partner.student_id})</span>
            </p>
            <p className="text-sm text-gray-500 mt-1">
              你们将作为一对被分配到同一书院
            </p>
          </div>

          <p className="text-sm font-medium text-gray-700 mb-2">配对后的匹配度:</p>
          <ResponsiveContainer width="100%" height={280}>
            <RadarChart data={mergedData} margin={{ top: 20, right: 40, bottom: 20, left: 40 }}>
            <PolarGrid />
            <PolarAngleAxis dataKey="name" tick={{ fontSize: 12 }} />
            <Radar name="合并匹配度" dataKey="合并匹配度" stroke="#10b981" fill="#10b981" fillOpacity={0.3} />
            </RadarChart>
          </ResponsiveContainer>

          {pairError && <p className="text-red-500 text-sm mt-2">{pairError}</p>}
        </div>
      );
    }

    // —————————— 收到邀请 ——————————
    if (pairStatus.pending_received && pairStatus.pending_received.length > 0) {
      const invitation = pairStatus.pending_received[0];
      const inviter = invitation.user1.id === userIdNum ? invitation.user2 : invitation.user1;

      return (
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-4">
          <h2 className="text-lg font-semibold text-gray-800 mb-2">📩 收到室友邀请</h2>
          <div className="bg-green-50 rounded-lg p-4 mb-4">
            <p className="text-gray-700">
              <span className="font-semibold">{inviter.name}</span>
              <span className="text-gray-500 ml-2">({inviter.student_id})</span>
              <span className="ml-2">邀请你成为室友</span>
            </p>
          </div>

          {!showPreview && (
            <button
              onClick={() => setShowPreview(true)}
              className="w-full bg-indigo-100 text-indigo-700 py-2 rounded-lg hover:bg-indigo-200 transition-colors mb-3 font-medium"
            >
              展开操作
            </button>
          )}

          {pairError && <p className="text-red-500 text-sm mb-2">{pairError}</p>}

          {showPreview && (
            <div className="flex gap-2 mb-2">
              <button
                onClick={() => handleAccept(invitation.id)}
                disabled={pairLoading}
                className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
              >
                {pairLoading ? '处理中...' : '接受邀请'}
              </button>
              <button
                onClick={() => handleReject(invitation.id)}
                disabled={pairLoading}
                className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 disabled:opacity-50 font-medium"
              >
                拒绝
              </button>
            </div>
          )}

          <p className="text-xs text-gray-500 mt-2">
            💡 接受后,你们将作为一对被分配到同一书院,匹配度按双方平均值计算。
          </p>

          {pairStatus.pending_received.length > 1 && (
            <p className="text-xs text-gray-400 mt-3 text-center">
              你还收到了 {pairStatus.pending_received.length - 1} 个其他邀请,处理完当前邀请后会显示
            </p>
          )}
        </div>
      );
    }

    // —————————— 已发出邀请,等待对方 ——————————
    if (pairStatus.pending_sent && pairStatus.pending_sent.length > 0) {
      const sent = pairStatus.pending_sent[0];
      const target = sent.user1.id === userIdNum ? sent.user2 : sent.user1;

      return (
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-4">
          <h2 className="text-lg font-semibold text-gray-800 mb-2">⏳ 等待对方回复</h2>
          <div className="bg-yellow-50 rounded-lg p-4 mb-4">
            <p className="text-gray-700">
              你已向 <span className="font-semibold">{target.name}</span>
              <span className="text-gray-500 ml-2">({target.student_id})</span>
              <span className="ml-2">发起室友申请</span>
            </p>
            <p className="text-sm text-gray-500 mt-1">请提醒对方登录系统并接受邀请</p>
          </div>

          {pairError && <p className="text-red-500 text-sm mb-2">{pairError}</p>}

          <button
            onClick={() => handleDissolve(sent.id, 'pending')}
            disabled={pairLoading}
            className="w-full bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 disabled:opacity-50 font-medium"
          >
            撤回邀请
          </button>
        </div>
      );
    }

    // —————————— 空状态:可申请 ——————————
    return (
      <div className="bg-white rounded-2xl shadow-lg p-8 mb-4">
        <h2 className="text-lg font-semibold text-gray-800 mb-2">🏠 申请室友(可选)</h2>
        <p className="text-gray-500 text-sm mb-4">
          选择一位同学作为室友,你们将作为一对被分配到同一书院,匹配度取双方平均值，但有小小可能因为宿舍人数（例如三人间）限制棒打鸳鸯
        </p>
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            placeholder="请输入室友学号"
            value={partnerInput}
            onChange={(e) => {
              setPartnerInput(e.target.value);
              setPairError('');
            }}
            disabled={pairLoading}
            className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:opacity-50"
          />
          <button
            onClick={handleInvite}
            disabled={pairLoading || !partnerInput.trim()}
            className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium"
          >
            {pairLoading ? '...' : '申请'}
          </button>
        </div>
        {pairError && <p className="text-red-500 text-sm">{pairError}</p>}
        <p className="text-xs text-gray-400 mt-3">
          💡 仅可邀请同性别已答题的同学,需双方确认才能生效
        </p>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-4">
          <h1 className="text-2xl font-bold text-gray-800 text-center mb-2">
            你好,{sessionStorage.getItem('user_name') || `用户 ${userId}`}!
          </h1>
          <p className="text-gray-500 text-center mb-8">以下是你与各书院的匹配度参考</p>

          <ResponsiveContainer width="100%" height={320}>
            <RadarChart data={chartData} margin={{ top: 20, right: 40, bottom: 20, left: 40 }}>
              <PolarGrid />
              <PolarAngleAxis dataKey="name" tick={{ fontSize: 12 }} />
              <Radar name="匹配度" dataKey="匹配度" stroke="#6366f1" fill="#6366f1" fillOpacity={0.3} />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8 mb-4">
          <h2 className="text-lg font-semibold text-gray-800 mb-6">匹配度排名</h2>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={sorted} layout="vertical" margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 12 }} />
              <YAxis type="category" dataKey="name" width={70} tick={{ fontSize: 13 }} />
              <Tooltip formatter={(v: unknown) => `${v}%`} />
              <Bar dataKey="匹配度" fill="#6366f1" radius={[0, 4, 4, 0]} />
          </BarChart>
          </ResponsiveContainer>
        </div>

        {/* ============ 室友配对区块 ============ */}
        {renderPairSection()}

        <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-6 text-center">
          <p className="text-indigo-700 font-medium">📌 实际录取结果将在6月21日所有人都填完后，以邮件形式告知</p>
          <p className="text-indigo-500 text-sm mt-1">我们的匹配度系统用于帮助同学了解与各书院的契合程度。
但最终分配并不只取决于匹配度高低，也与各书院的名额有关——同一书院会从所有适合该书院的同学里在名额范围内选择最合适的人。
因此，匹配度仅作为参考。</p>
        </div>

        {/* ============ 重新答题(放在最底部,不显眼) ============ */}
        <div className="text-center mt-6 mb-4">
          <button
            onClick={handleRestart}
            disabled={restartLoading}
            className="text-sm text-gray-400 hover:text-gray-600 underline disabled:opacity-50"
          >
            {restartLoading ? '处理中...' : '重新答题'}
          </button>
        </div>
      </div>
    </div>
  );
}
